import { motion } from "framer-motion";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

import type { ExperienceEntry } from "@/constants";
import { cn } from "@/utils";

type ExperienceCardProps = {
	experience: ExperienceEntry;
	onClose?: () => void;
};

export function ExperienceOrbitCard({
	experience,
	onClose,
}: ExperienceCardProps) {
	const cardRef = useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
				}
			},
			{ threshold: 0.3 },
		);

		if (cardRef.current) {
			observer.observe(cardRef.current);
		}

		return () => {
			if (cardRef.current) {
				observer.unobserve(cardRef.current);
			}
		};
	}, []);

	return (
		<article
			ref={cardRef}
			className="relative flex-shrink-0 overflow-hidden rounded-[20px] sm:rounded-[24px] md:rounded-[28px] lg:rounded-[32px] border border-white/10 bg-white/5 p-4 backdrop-blur transition-all duration-300 hover:border-white/20 hover:bg-white/10 w-full md:w-[700px] max-h-[80vh] overflow-y-auto hide-scrollbar"
		>
			{onClose && (
				<button
					onClick={onClose}
					className="absolute top-2 right-2 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white/70 transition-all duration-200 hover:border-white/40 hover:bg-white/10 hover:text-white"
					aria-label="Close"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<line x1="18" y1="6" x2="6" y2="18" />
						<line x1="6" y1="6" x2="18" y2="18" />
					</svg>
				</button>
			)}
			<div className="relative z-10">
				<div className="flex items-end justify-between gap-3 border-b border-white/5 pb-3">
					<div className="flex items-center gap-3">
						<div className={cn(
              "relative h-12 w-12 overflow-hidden rounded-xl border border-white/15",
              `bg-${experience.iconBg}`
            )}>
							<Image
								src={experience.icon}
								alt={experience.company_name}
								sizes="48px"
								className={cn(
                  "object-contain p-2 w-full h-full aspect-square",
                )}
                style={{ 
                  backgroundColor: experience.iconBg,
                }}
							/>
						</div>
						<div>
							<p className="text-xs uppercase tracking-[0.35em] text-white/50">
								{experience.date}
							</p>
							<h3 className="text-lg font-semibold text-white">
								{experience.title}
							</h3>
							<p className="text-xs text-white/70">{experience.company_name}</p>
						</div>
					</div>
					<div className="text-right">
						<p className="text-xs uppercase tracking-[0.35em] text-white/40">
							Location
						</p>
						<p className="text-xs font-semibold text-white">
							{experience.location}
						</p>
					</div>
				</div>

				<p className="mt-3 text-sm text-white/75 leading-relaxed">{experience.mission}</p>

				<div className="mt-3 flex flex-wrap gap-2">
					{experience.stack.map((item) => (
						<motion.span
							key={`${experience.id}-${item}`}
							whileHover={{ scale: 1.05, y: -2 }}
							className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-[0.25em] text-white/70"
						>
							{item}
						</motion.span>
					))}
				</div>

				<ul className="mt-3 space-y-2 text-white/80">
					{experience.points.map((point, idx) => (
						<motion.li
							key={`${experience.id}-${idx}`}
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -20 }}
							transition={{ delay: idx * 0.1 }}
							className="flex gap-2 text-sm"
						>
							<motion.span
								className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#ffe36c] flex-shrink-0"
								animate={{
									boxShadow: [
										"0 0 0px rgba(255,227,108,0.5)",
										"0 0 10px rgba(255,227,108,0.8)",
										"0 0 0px rgba(255,227,108,0.5)",
									],
								}}
								transition={{
									duration: 2,
									repeat: Infinity,
									delay: idx * 0.2,
								}}
							/>
							<p className="leading-snug">{point}</p>
						</motion.li>
					))}
				</ul>

				<div className="mt-3 rounded-xl border border-white/10 bg-black/30 p-3">
					<p className="text-xs uppercase tracking-[0.35em] text-white/40">
						Impact
					</p>
					<p className="mt-1.5 text-sm text-white/80 leading-relaxed">{experience.impact}</p>
				</div>
			</div>
		</article>
	);
}

